import { supabase, assertOk } from './supabaseClient'

/**
 * PANEL DE SUPER ADMINISTRACIÓN
 * ------------------------------------------------------------------
 * Todas las funciones de este archivo son RPC `SECURITY DEFINER` que ya
 * existen en Supabase y validan por sí mismas que quien llama tenga
 * `profiles.is_super_admin = true`. Por eso se invocan con el cliente
 * normal (clave anónima); aquí nunca se usa la service_role key.
 */

/** Fila devuelta por `admin_users_overview()`. */
export interface AdminUserRow {
  user_id: string
  organization_id: string
  full_name: string | null
  email: string | null
  organization_name: string | null
  created_at: string
  leads_count: number
  forms_count: number
  landings_count: number
  is_blocked: boolean
}

/** Resultado de `admin_delete_organization()`. */
export interface DeleteOrganizationResult {
  deleted_users?: number
  kept_users?: number
}

/** Indica si el perfil del usuario actual es super administrador. */
export async function fetchIsSuperAdmin(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('profiles')
    .select('is_super_admin')
    .eq('id', userId)
    .maybeSingle()

  // Un fallo aquí nunca debe impedir el acceso normal a la aplicación.
  if (error) {
    console.error('[AVA] No se ha podido leer is_super_admin:', error.message)
    return false
  }
  return data?.is_super_admin === true
}

/** Listado completo de usuarios y organizaciones de la plataforma. */
export async function fetchUsersOverview(): Promise<AdminUserRow[]> {
  const data = assertOk(await supabase.rpc('admin_users_overview'))
  return (data as AdminUserRow[]) ?? []
}

/**
 * Vacía el contenido de una organización (leads, notas, formularios, landings)
 * y recrea estados y fuentes por defecto. No toca al usuario ni sus claves.
 */
export async function resetOrganization(organizationId: string): Promise<void> {
  assertOk(
    await supabase.rpc('admin_reset_organization', { p_org_id: organizationId }),
  )
}

/** Bloquea el acceso de un usuario (`banned_until`), sin borrar datos. */
export async function blockUser(userId: string): Promise<void> {
  assertOk(await supabase.rpc('admin_block_user', { p_user_id: userId }))
}

/** Reactiva el acceso de un usuario previamente bloqueado. */
export async function unblockUser(userId: string): Promise<void> {
  assertOk(await supabase.rpc('admin_unblock_user', { p_user_id: userId }))
}

/**
 * Elimina una organización completa en cascada. El usuario solo se borra si
 * no pertenece a ninguna otra organización.
 */
export async function deleteOrganization(
  organizationId: string,
): Promise<DeleteOrganizationResult> {
  const data = assertOk(
    await supabase.rpc('admin_delete_organization', { p_org_id: organizationId }),
  )
  return (data as DeleteOrganizationResult) ?? {}
}
